import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import NotificationBar from "../components/home/NotifictionBar";
import searchFormBg from "../assets/searchform-bg.jpg";

import Leftsidebar from "../components/vehicles/Leftsidebar";
import VehicleCard from "../components/VehicleCard";
import VehicleListCard from "../components/VehicleListCard";
import api from "../lib/api.ts";
import placeholderImage from "../assets/placeholder.svg";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import usePageMeta from "../hooks/usePageMeta.js";

const Search = () => {
  usePageMeta("vehicles");

  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("search") || "");
  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  // Extract auctionId and laneId from URL parameters
  const auctionId = searchParams.get("auctionId");
  const laneId = searchParams.get("laneId");

  // Reset URL params processed flag when URL parameters change
  useEffect(() => {
    setUrlParamsProcessed(false);
  }, [auctionId, laneId]);

  // console.log("searchParams", searchParams);
  // console.log("auctionId", auctionId);
  // console.log("laneId", laneId);
  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleSearch = () => {
    // Search functionality handled by API calls
  };
  // test message
  const [leftVisible, setLeftVisible] = useState(false);

  const [filters, setFilters] = useState({
    make: searchParams.get("makeId") || "",
    model: "",
    yearStart: "",
    yearEnd: "",
  });

  // State for makes and models data from API
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);

  // State for vehicles data from API
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // State for wishlist data
  const [wishlistData, setWishlistData] = useState([]);

  // State for auctions data from API
  const [auctions, setAuctions] = useState([]);
  const [selectedAuction, setSelectedAuction] = useState("");
  const [selectedLane, setSelectedLane] = useState("");
  const [urlError, setUrlError] = useState("");
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  // Sync URL parameters with filters when component mounts or URL changes
  useEffect(() => {
    const makeIdFromUrl = searchParams.get("makeId");
    if (makeIdFromUrl && makeIdFromUrl !== filters.make) {
      // Validate that the makeId exists in our makes array
      const isValidMake = makes.some(
        (make) => make.id.toString() === makeIdFromUrl
      );
      if (isValidMake) {
        setFilters((prev) => ({
          ...prev,
          make: makeIdFromUrl,
          model: "", // Reset model when make changes
        }));
      } else {
        // If invalid makeId, remove it from URL and show error
        console.warn(`Invalid makeId in URL: ${makeIdFromUrl}`);
        setUrlError(
          `Invalid make ID "${makeIdFromUrl}" found in URL. It has been removed.`
        );
        const params = new URLSearchParams(searchParams);
        params.delete("makeId");
        const newUrl = `${window.location.pathname}${
          params.toString() ? "?" + params.toString() : ""
        }`;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [searchParams, makes]);

  // Sync search parameter from URL with query state
  useEffect(() => {
    const searchFromUrl = searchParams.get("search");
    if (searchFromUrl !== null && searchFromUrl !== query) {
      setQuery(searchFromUrl);
    }
  }, [searchParams, query]);

  // Auto-apply featured filter from URL parameter
  useEffect(() => {
    const featuredParam = searchParams.get("featured");
    if (featuredParam !== null && sidebarRef.current) {
      const normalized = (() => {
        const val = String(featuredParam).toLowerCase();
        if (val === "1" || val === "yes" || val === "true") return "yes";
        if (val === "0" || val === "no" || val === "false") return "no";
        return "";
      })();
      // Set the featured filter in the sidebar to trigger parent sync
      if (typeof sidebarRef.current.setFeaturedFilter === "function") {
        sidebarRef.current.setFeaturedFilter(normalized);
      }
    }
  }, [searchParams]);

  // Handle auctionId and laneId from URL parameters
  useEffect(() => {
    if (auctionId && auctions.length > 0) {
      // Find the auction and set it as selected
      const foundAuction = auctions.find(
        (auction) => auction.id.toString() === auctionId
      );
      if (foundAuction) {
        setSelectedAuction(auctionId);
      }
    }
  }, [auctionId, auctions]);

  useEffect(() => {
    if (laneId && selectedAuction && auctions.length > 0) {
      // Find the lane within the selected auction and set it as selected
      const selectedAuctionData = auctions.find(
        (auction) => auction.id.toString() === selectedAuction
      );
      if (selectedAuctionData?.auctionLanes) {
        const foundLane = selectedAuctionData.auctionLanes.find((lane) => {
          const laneIdMatch = lane.laneId?.toString() === laneId;
          const idMatch = lane.id?.toString() === laneId;

          return laneIdMatch || idMatch;
        });
        if (foundLane) {
          // Use the original laneId from URL, not the found lane's ID
          setSelectedLane(laneId);
        }
      }
    }
  }, [laneId, selectedAuction, auctions]);

  // Mark URL parameters as processed when both auction and lane are set (or when no URL params exist)
  useEffect(() => {
    if (auctions.length > 0) {
      if (auctionId && laneId) {
        // Both auctionId and laneId are in URL - wait for both to be processed
        if (selectedAuction && selectedLane) {
          setUrlParamsProcessed(true);
        }
      } else if (auctionId && !laneId) {
        // Only auctionId is in URL - wait for auction to be processed
        if (selectedAuction) {
          setUrlParamsProcessed(true);
        }
      } else {
        // No URL parameters - can proceed immediately
        setUrlParamsProcessed(true);
      }
    }
  }, [auctionId, laneId, selectedAuction, selectedLane, auctions.length]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (filters.make) {
      params.set("makeId", filters.make);
    } else {
      params.delete("makeId");
    }

    // Update URL with auction and lane parameters
    if (selectedAuction) {
      params.set("auctionId", selectedAuction);
    } else {
      params.delete("auctionId");
    }

    if (selectedLane) {
      params.set("laneId", selectedLane);
    } else {
      params.delete("laneId");
    }

    // Update URL without page reload
    const newUrl = `${window.location.pathname}${
      params.toString() ? "?" + params.toString() : ""
    }`;
    window.history.replaceState({}, "", newUrl);
  }, [filters.make, selectedAuction, selectedLane, searchParams]);

  // Fetch makes data from API
  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await api.get("/makes");
        setMakes(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching makes:", error);
        setMakes([]); // Set empty array on error
      }
    };

    fetchMakes();
  }, []);

  // Fetch auctions data from API
  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        const response = await api.get("/auctions/paginated", {
          params: {
            type: "upcomming",

            orderBy: "startDate",
            order: "ASC",
            "filters[isActive]": true,
          },
        });
        const auctionsData = Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        // Process auctions to extract lanes from the new API structure
        const processedAuctions = auctionsData.map((auction) => {
          // Extract lanes from auctionLanes array
          let lanes = [];
          if (auction.auctionLanes && Array.isArray(auction.auctionLanes)) {
            lanes = auction.auctionLanes.map((lane) => {
              // Get the first vehicle's run number from auctionLaneVehicles
              const firstVehicle = lane.auctionLaneVehicles?.[0];
              const runNo = firstVehicle?.runNo || 0;

              return {
                id: lane.id, // This is the auctionLaneId we'll use for filtering vehicles
                laneId: lane.laneId, // This is the actual lane identifier from the lane table
                auctionId: lane.auctionId || auction.id,
                runNo: runNo,
                lane: lane.lane, // Full lane object with name, etc.
                auction: auction,
              };
            });
          }

          return {
            ...auction,
            auctionLanes: lanes,
          };
        });

        setAuctions(processedAuctions);
      } catch (error) {
        console.error("Error fetching auctions:", error);
        setAuctions([]); // Set empty array on error
      }
    };

    fetchAuctions();
  }, []);

  // Fetch wishlist data from API
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user?.id) {
        setWishlistData([]);
        return;
      }

      try {
        const response = await api.get("/wishlist");
        const wishlist = response.data?.data || response.data;

        if (wishlist && Array.isArray(wishlist)) {
          setWishlistData(wishlist);
        } else {
          setWishlistData([]);
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
        setWishlistData([]);
      }
    };

    fetchWishlist();
  }, [user?.id]);

  // Fetch models data when make changes
  useEffect(() => {
    const fetchModels = async () => {
      if (!filters.make) {
        setModels([]);
        return;
      }

      try {
        const response = await api.get(`/models?makeId=${filters.make}`);
        setModels(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching models:", error);
        setModels([]); // Set empty array on error
      }
    };

    fetchModels();
  }, [filters.make, searchParams]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    // If make changes, reset model
    if (name === "make") {
      setFilters({ ...filters, [name]: value, model: "" });
    } else {
      setFilters({ ...filters, [name]: value });
    }
  };

  const [activeFilters, setActiveFilters] = useState({});

  const [sidebarFilters, setSidebarFilters] = useState({});
  const [sidebarFilterData, setSidebarFilterData] = useState({});
  const sidebarRef = useRef();

  // View toggle state
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"

  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [sortBy, setSortBy] = useState("id-desc");

  // Calculate pagination info for display
  const displayCount = Object.keys(sidebarFilters).some(
    (key) =>
      sidebarFilters[key] &&
      (Array.isArray(sidebarFilters[key])
        ? sidebarFilters[key].length > 0
        : sidebarFilters[key])
  )
    ? vehicles.length
    : totalVehicles;
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, displayCount);
  const currentVehicles = vehicles; // API already returns paginated results

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of results
    document
      .querySelector("#browse-results")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(parseInt(newPageSize));
    setCurrentPage(1); // Reset to first page
  };

  // Handle sort change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1); // Reset to first page
  };

  // Handle auction change
  const handleAuctionChange = (newAuction) => {
    // console.log("=== AUCTION CHANGE ===");
    // console.log("Previous Auction ID:", selectedAuction);
    // console.log("New Auction ID:", newAuction);

    if (newAuction) {
      const selectedAuctionData = auctions.find(
        (auction) => auction.id.toString() === newAuction
      );
      if (selectedAuctionData) {
        // console.log("New Auction Data:", selectedAuctionData);
        // console.log(
        //   "Available Lanes Count:",
        //   selectedAuctionData.auctionLanes?.length || 0
        // );
        if (
          selectedAuctionData.auctionLanes &&
          selectedAuctionData.auctionLanes.length > 0
        ) {
          console.log("=== ALL LANES IN SELECTED AUCTION ===");
          console.log(
            "Auction Name:",
            selectedAuctionData.name || selectedAuctionData.auction
          );
          console.log("Total Lanes:", selectedAuctionData.auctionLanes.length);
          console.log("Lanes Details:");

          // Create a table-like format for better readability
          console.table(
            selectedAuctionData.auctionLanes.map((lane, index) => ({
              "#": index + 1,
              "Auction Lane ID": lane.id,
              "Lane ID": lane.laneId,
              "Lane Name": lane.lane?.name || `Lane ${lane.laneId}`,
              "Run Number": lane.runNo || "N/A",
              "Vehicle Count": lane.auctionLaneVehicles?.length || 0,
              "Auction ID": lane.auctionId || selectedAuctionData.id,
            }))
          );

          // Also log individual lane details for debugging
          selectedAuctionData.auctionLanes.forEach((lane, index) => {
            console.log(`Lane ${index + 1} Details:`, {
              auctionLaneId: lane.id,
              laneId: lane.laneId,
              laneName: lane.lane?.name || `Lane ${lane.laneId}`,
              runNo: lane.runNo,
              vehicleCount: lane.auctionLaneVehicles?.length || 0,
              auctionId: lane.auctionId || selectedAuctionData.id,
              fullLaneObject: lane,
            });
          });
          console.log("=====================================");
        } else {
          console.log("No lanes available for this auction");
        }
      }
    } else {
      console.log("Auction deselected - showing all auctions");
    }
    console.log("=====================");

    setSelectedAuction(newAuction);
    setSelectedLane(""); // Reset lane when auction changes
    setCurrentPage(1); // Reset to first page
  };

  // Handle lane selection
  const handleLaneSelect = (laneId) => {
    const newSelectedLane = selectedLane === laneId ? "" : laneId;
    console.log("=== LANE SELECTION ===");
    console.log("Previous Lane ID:", selectedLane);
    console.log("New Lane ID:", newSelectedLane);
    console.log("Action:", newSelectedLane ? "Selected" : "Deselected");

    // Find and log lane details
    if (newSelectedLane && selectedAuction) {
      const selectedAuctionData = auctions.find(
        (auction) => auction.id.toString() === selectedAuction
      );
      if (selectedAuctionData?.auctionLanes) {
        const selectedLaneData = selectedAuctionData.auctionLanes.find(
          (lane) =>
            lane.id?.toString() === newSelectedLane ||
            lane.laneId?.toString() === newSelectedLane
        );
        if (selectedLaneData) {
          console.log("Selected Lane Details:", {
            id: selectedLaneData.id,
            laneId: selectedLaneData.laneId,
            laneName:
              selectedLaneData.lane?.name || `Lane ${selectedLaneData.laneId}`,
            runNo: selectedLaneData.runNo,
            vehicleCount: selectedLaneData.auctionLaneVehicles?.length || 0,
          });
        }
      }
    }
    console.log("=====================");

    setSelectedLane(newSelectedLane);
  };

  // Handle sidebar filter changes
  const handleSidebarFiltersChange = (filters, filterData) => {
    setSidebarFilters(filters);
    setSidebarFilterData(filterData);
  };

  // Fetch vehicles data from API
  useEffect(() => {
    const fetchVehicles = async () => {
      // Don't fetch vehicles until URL parameters are processed (if they exist)
      if ((auctionId || laneId) && !urlParamsProcessed) {
        return;
      }

      setLoading(true);
      try {
        // Prepare API parameters with proper sorting and filtering
        const apiParams = {
          type: "upcomming",
          page: currentPage,
          limit: pageSize,
          search: query || "",
          // Main filters

          "filters[ProductStatus]": "2,3,4",
          "filters[makeName]": filters.make || "",
          "filters[MakeModelName]": filters.model || "",
          "filters[yearStart]": filters.yearStart || "",
          "filters[yearEnd]": filters.yearEnd || "",
          "filters[search]": query || "",
          // Sidebar filters from the left sidebar
          // Note: Featured filter is handled client-side after API response
          ...(sidebarFilters.bodyTypes?.length && {
            "filters[BodyType]": sidebarFilters.bodyTypes.join(","),
          }),
          ...(sidebarFilters.drivelineTypes?.length && {
            "filters[DriveLineType]": sidebarFilters.drivelineTypes.join(","),
          }),
          ...(sidebarFilters.transmissionTypes?.length && {
            "filters[TransmissionType]":
              sidebarFilters.transmissionTypes.join(","),
          }),
          ...(sidebarFilters.fuelTypes?.length && {
            "filters[fuelType]": sidebarFilters.fuelTypes.join(","),
          }),
          ...(sidebarFilters.engines?.length && {
            "filters[engine]": sidebarFilters.engines.join(","),
          }),
          ...(sidebarFilters.highlights?.length && {
            "filters[Highlight]": sidebarFilters.highlights.join(","),
          }),
          ...(sidebarFilters.cylinders?.length && {
            "filters[cylinder]": sidebarFilters.cylinders.join(","),
          }),
          ...(sidebarFilters.exteriorColors?.length && {
            "filters[exteriorColor]": sidebarFilters.exteriorColors.join(","),
          }),
          ...(sidebarFilters.interiorColors?.length && {
            "filters[interiorColor]": sidebarFilters.interiorColors.join(","),
          }),
          ...(sidebarFilters.primaryDamageTypes?.length && {
            "filters[primaryDamageType]":
              sidebarFilters.primaryDamageTypes.join(","),
          }),
          // Auction filter
          ...(selectedAuction && {
            "filters[auction]": selectedAuction,
          }),
          // Lane filter - use auctionLaneId for filtering
          ...(selectedLane && {
            "filters[lane]": selectedLane,
          }),
        };

        // Add sorting parameters based on sortBy selection
        if (sortBy) {
          switch (sortBy) {
            case "id-desc":
              apiParams.orderBy = "id";
              apiParams.order = "desc";
              break;
            case "id-asc":
              apiParams.orderBy = "id";
              apiParams.order = "asc";
              break;
            case "price-low":
              apiParams.orderBy = "startingBid";
              apiParams.order = "asc";
              break;
            case "price-high":
              apiParams.orderBy = "startingBid";
              apiParams.order = "desc";
              break;
            case "year-new":
              apiParams.orderBy = "year";
              apiParams.order = "desc";
              break;
            case "year-old":
              apiParams.orderBy = "year";
              apiParams.order = "asc";
              break;
            case "mileage-low":
              apiParams.orderBy = "mileage";
              apiParams.order = "asc";
              break;
            case "mileage-high":
              apiParams.orderBy = "mileage";
              apiParams.order = "desc";
              break;
            default:
              apiParams.orderBy = "id";
              apiParams.order = "desc";
          }
        } else {
          apiParams.orderBy = "id";
          apiParams.order = "desc";
        }

        const response = await api.get("/products/paginated", {
          params: apiParams,
        });

        // Extract pagination data from API response
        const apiData = response.data;
        const totalCount =
          apiData.total || apiData.totalCount || apiData.count || 0;
        const totalPagesFromApi =
          apiData.totalPages ||
          apiData.last_page ||
          Math.ceil(totalCount / pageSize);

        setTotalVehicles(totalCount);
        setTotalPages(totalPagesFromApi);

        // Helper function to safely extract string values
        const safeString = (value, fallback = "") => {
          if (value === null || value === undefined) return fallback;
          if (typeof value === "object" && value.name) return value.name;
          if (typeof value === "string") return value;
          if (typeof value === "number") return value.toString();
          return fallback;
        };

        // Map API response to match sampleVehicles structure
        const mappedVehicles = Array.isArray(response.data?.data)
          ? response.data.data.map((product) => ({
              id: product.id,
              title: safeString(product?.name),
              slug: safeString(product?.slug),
              lotNo: safeString(product?.lotNumber),
              condition: safeString(product?.primaryDamageType?.name),
              miles: Number(product?.mileage),
              fuelType: safeString(product?.fuelType?.name),
              engineSize: safeString(product?.engine?.name),
              startBidding: Number(product?.startingBid),
              latestBid: Number(product?.latestBid),
              startingBid: Number(product?.startingBid), // Keep original for backward compatibility
              currentBid: Number(product?.latestBid), // Keep original for backward compatibility
              buyNowPrice: Number(product?.buyNowPrice),
              image: safeString(
                Array.isArray(product?.images) && product?.images?.length > 0
                  ? product?.images[0]?.imageUrl ||
                      product?.images[0]?.image ||
                      product?.images[0]
                  : placeholderImage
              ),
              isRunAndDrive: Boolean(
                product.isRunAndDrive !== undefined
                  ? product?.isRunAndDrive
                  : product?.run_and_drive !== undefined
                  ? product?.run_and_drive
                  : safeString(product.condition).toLowerCase().includes("run")
              ),
              // Additional fields that might be useful
              year: safeString(product?.year),
              make: safeString(product?.make),
              model: safeString(product.model),
              vin: (() => {
                const vin = safeString(product?.vinNumber);
                if (vin && vin.length > 4) {
                  return "*****" + vin.slice(-4);
                }
                return vin;
              })(),
              sellerName: safeString(product?.seller?.user?.name),
              auctionStatus: safeString(
                product.auctionStatus || product.auction_status
              ),
              estimatedRetailValue: Number(
                product.estimatedRetailValue ||
                  product.estimated_retail_value ||
                  0
              ),
              cylinder: safeString(product?.cylinder?.name || "-"),
              transmission: safeString(product?.TransmissionType?.name || "-"),
              driveType: safeString(product?.driveType?.name || "-"),
              bodyType: safeString(product?.bodyType?.name || "-"),
              exteriorColor: safeString(product?.exteriorColor?.name || "-"),
              interiorColor: safeString(product?.interiorColor?.name || "-"),
              isFeatured: Number(product?.isFeatured || 0), // Add featured field from API
              runNo: product?.auctionLanesVehicles[0]?.runNo || product?.runNo,
              auctionLaneId: (() => {
                const laneId =
                  product?.auctionLanesVehicles[0]?.auctionLane?.id ||
                  product?.auctionLanesVehicles[0]?.id;
                return laneId;
              })(),
              // Pass the entire auctionLanesVehicles array
              auctionLanesVehicles: product?.auctionLanesVehicles || [],

              // Pass ProductStatus object (not just the name)
              ProductStatus: product?.ProductStatus,
              // Additional fields from API response
              bidIncrement: Number(product?.bidIncrement),
              reservedPrice: Number(product?.reservedPrice),
              marketValue: Number(product?.marketValue),
              // Status fields
              productAuctionStatus: safeString(
                product?.ProductAuctionStatus?.name || ""
              ),
              productPaymentStatus: safeString(
                product.ProductPaymentStatus?.name || ""
              ),
              productStatus: safeString(product.ProductStatus?.name || ""),
              // IDs for reference
              highlights: (() => {
                // Check different possible highlight structures
                const highlightSources = [
                  product?.highlights,
                  product?.Highlights,
                  product?.highlight,
                  product?.Highlight,
                ];

                for (const source of highlightSources) {
                  if (Array.isArray(source) && source.length > 0) {
                    return source
                      .map((h) => {
                        if (typeof h === "object" && h !== null) {
                          return {
                            id: h.id,
                            name: safeString(h.name),
                            textColor: h.textColor || "#ffffff",
                            bgColor: h.bgColor || "#f97316",
                          };
                        }
                        return safeString(h);
                      })
                      .filter((h) => h && (typeof h === "object" ? h.name : h));
                  }
                  // Handle single highlight object
                  if (
                    typeof source === "object" &&
                    source !== null &&
                    source.name
                  ) {
                    return [
                      {
                        id: source.id,
                        name: safeString(source.name),
                        textColor: source.textColor || "#ffffff",
                        bgColor: source.bgColor || "#f97316",
                      },
                    ];
                  }
                }

                return [];
              })(),
            }))
          : [];

        // Apply featured filter if selected (use boolean truthiness for robustness)
        let filteredVehicles = mappedVehicles;
        if (sidebarFilters.featured) {
          const wantFeatured = sidebarFilters.featured === "yes";
          filteredVehicles = mappedVehicles.filter(
            (vehicle) => Boolean(vehicle.isFeatured) === wantFeatured
          );
        }

        setVehicles(filteredVehicles);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
        setVehicles([]); // Fallback to empty array
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, [
    currentPage,
    pageSize,
    sortBy,
    filters.make,
    filters.model,
    filters.yearStart,
    filters.yearEnd,
    query,
    sidebarFilters,
    selectedAuction,
    selectedLane,
    urlParamsProcessed,
    auctionId,
    laneId,
  ]);

  // Function to remove a specific sidebar filter
  const removeSidebarFilter = (filterType, filterId) => {
    // Update sidebar component state
    if (sidebarRef.current) {
      sidebarRef.current.removeFilter(filterType, filterId);
    }
  };

  // Function to clear all filters of a specific type
  const clearFilterType = (filterType) => {
    // Update sidebar component state
    if (sidebarRef.current) {
      sidebarRef.current.clearFilterType(filterType);
    }
  };

  // Get filter name by ID
  const getFilterName = (filterType, filterId) => {
    const filterArray = sidebarFilterData[filterType] || [];
    const filterItem = filterArray.find((item) => item.id === filterId);
    return filterItem ? filterItem.name : `Unknown ${filterType}`;
  };

  // Get filter type display name
  const getFilterTypeDisplayName = (filterType) => {
    return filterType
      .replace(/([A-Z])/g, " $1")
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase())
      .replace(" types", "");
  };

  // Helper function to get make name by ID
  const getMakeName = (makeId) => {
    const make = makes.find((m) => m.id == makeId);
    return make ? make.name : makeId;
  };

  // Helper function to get model name by ID
  const getModelName = (modelId) => {
    const model = models.find((m) => m.id == modelId);
    return model ? model.name : modelId;
  };

  // Helper function to get lane name by ID
  const getLaneName = (laneId) => {
    // Find the lane across all auctions
    for (const auction of auctions) {
      const lane = auction.auctionLanes?.find((l) => l.id == laneId);
      if (lane) {
        return `${lane.lane?.name || `Lane ${lane.laneId}`}`;
      }
    }
    return `Lane ${laneId}`;
  };

  // Helper function to get display value for filters
  const getFilterDisplayValue = (key, value) => {
    switch (key) {
      case "make":
        return getMakeName(value);
      case "model":
        return getModelName(value);
      case "yearStart":
        return value; // Year is already a display value
      case "yearEnd":
        return value; // Year is already a display value
      default:
        return value;
    }
  };

  // Get all selected filter names for a filter type
  const getSelectedFilterNames = (filterType, selectedIds) => {
    if (filterType === "featured") {
      // For featured filter, selectedIds is a string, not an array
      return selectedIds ? [selectedIds === "yes" ? "Yes" : "No"] : [];
    }
    if (!selectedIds || selectedIds.length === 0) return [];
    return selectedIds.map((id) => getFilterName(filterType, id));
  };

  return (
    <>
      <NotificationBar />
      <section className="min-h-400 max-w-7xl mx-auto">
        <div
          className="w-full p-2 sm:p-4 flex justify-center"
          style={{
            backgroundImage: `url(${searchFormBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="w-full">
            {/* <SearchFilter /> */}

            <form
              className={`flex flex-col lg:flex-row items-stretch lg:items-center gap-2 sm:gap-4 mb-4 sm:mb-6 w-full bg-darkGraybg bg-opacity-60 rounded-lg p-3 sm:p-6 ${
                isRTL ? "lg:flex-row-reverse" : ""
              }`}
              dir={isRTL ? "rtl" : "ltr"}
              lang={language}
              onSubmit={(e) => {
                e.preventDefault();
                handleSearch();
                setActiveFilters({
                  query,
                  ...filters,
                });
              }}
            >
              {/* Search Input - Bigger size */}
              <input
                type="text"
                className={`w-full lg:w-2/6 ${
                  isRTL ? "pr-3 pl-3" : "px-3"
                } py-2 rounded text-sm sm:text-base`}
                placeholder={translate("search.searchByLotVin")}
                value={query}
                onChange={handleInputChange}
                name="query"
              />

              {/* Filter Selects - Equal sizes for all 4 dropdowns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 w-full lg:w-3/6">
                <select
                  className={`w-full ${
                    isRTL ? "pr-3 pl-3" : "px-3"
                  } py-2 rounded text-sm sm:text-base`}
                  name="make"
                  value={filters.make}
                  onChange={handleFilterChange}
                >
                  <option value="">{translate("search.make")}</option>
                  {Array.isArray(makes) &&
                    makes.map((make) => (
                      <option key={make.id} value={make.id}>
                        {make.name}
                      </option>
                    ))}
                </select>
                <select
                  className={`w-full ${
                    isRTL ? "pr-3 pl-3" : "px-3"
                  } py-2 rounded text-sm sm:text-base`}
                  name="model"
                  value={filters.model}
                  onChange={handleFilterChange}
                  disabled={!filters.make}
                >
                  <option value="">
                    {!filters.make
                      ? translate("search.selectMakeFirst")
                      : translate("search.model")}
                  </option>
                  {Array.isArray(models) &&
                    models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                </select>
                <select
                  className={`w-full ${
                    isRTL ? "pr-3 pl-3" : "px-3"
                  } py-2 rounded text-sm sm:text-base`}
                  name="yearStart"
                  value={filters.yearStart}
                  onChange={handleFilterChange}
                >
                  <option value="">{translate("search.yearFrom")}</option>
                  {Array.from({ length: 30 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
                <select
                  className={`w-full ${
                    isRTL ? "pr-3 pl-3" : "px-3"
                  } py-2 rounded text-sm sm:text-base`}
                  name="yearEnd"
                  value={filters.yearEnd}
                  onChange={handleFilterChange}
                >
                  <option value="">{translate("search.yearTo")}</option>
                  {Array.from({ length: 30 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>
              {/* Action Buttons - Smaller size to accommodate bigger search input */}
              <div
                className={`flex flex-col sm:flex-row gap-2 sm:gap-4 w-full lg:w-1/6 ${
                  isRTL ? "sm:flex-row-reverse" : ""
                }`}
              >
                <button
                  type="submit"
                  className="w-full px-3 py-2 text-sm sm:text-base bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                >
                  {translate("search.search")}
                </button>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-sm sm:text-base text-white bg-darkGraybg rounded hover:bg-gray-700 transition-colors"
                  onClick={() => {
                    setQuery("");
                    setFilters({
                      make: "",
                      model: "",
                      yearStart: "",
                      yearEnd: "",
                    });
                    setActiveFilters({});
                    setSidebarFilters({});
                    setSidebarFilterData({});
                    setSelectedAuction("");
                    setSelectedLane("");
                    setCurrentPage(1);
                    setSortBy("id-desc");
                    setPageSize(24);
                    setUrlError(""); // Clear URL error message

                    // Clear URL parameters
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, "", newUrl);

                    // Reset sidebar component's internal state
                    if (
                      sidebarRef.current &&
                      sidebarRef.current.resetAllFilters
                    ) {
                      sidebarRef.current.resetAllFilters();
                    }
                  }}
                >
                  {translate("search.clear")}
                </button>
              </div>
            </form>

            {/* URL Error Message */}
            {urlError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{urlError}</span>
                  <button
                    onClick={() => setUrlError("")}
                    className="ml-auto text-red-500 hover:text-red-700"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="w-full sm:w-1/5">
                {/* Advanced Search Button */}
                <div className="flex justify-start px-2 sm:px-4 mb-4">
                  <button
                    type="button"
                    className="w-full sm:w-auto px-6 sm:px-10 py-2 sm:py-3 text-sm sm:text-lg bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                    onClick={() => setLeftVisible((v) => !v)}
                  >
                    {leftVisible
                      ? translate("search.hideAdvancedSearch")
                      : translate("search.showAdvancedSearch")}
                  </button>
                </div>
              </div>
              <div className="w-full sm:w-4/5">
                {/* Active Search Filters */}
                {/* <div id="query-results" className="flex gap-1">
                  {Object.entries(activeFilters)
                    .filter(([key, value]) => value)
                    .map(([key, value], idx) => (
                      <span
                        key={idx}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {key.charAt(0).toUpperCase() + key.slice(1)}:{" "}
                        {getFilterDisplayValue(key, value)}
                      </span>
                    ))}
                </div> */}

                {/* Selected Sidebar Filters Tags - Grouped with Individual Delete */}
                {Object.entries(sidebarFilters).some(
                  ([key, value]) => value && value.length > 0
                ) && (
                  <div className="bg-darkGraybg bg-opacity-60 rounded-lg p-3 sm:p-4 mx-2 sm:mx-4 mb-4">
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(sidebarFilters).map(
                        ([filterType, selectedIds]) => {
                          // Handle different filter types
                          if (filterType === "featured") {
                            if (!selectedIds) return null;
                          } else {
                            if (!selectedIds || selectedIds.length === 0)
                              return null;
                          }

                          const selectedNames = getSelectedFilterNames(
                            filterType,
                            selectedIds
                          );

                          return (
                            <div key={filterType} className="relative group">
                              <span className="inline-flex items-center bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium hover:bg-orange-600 transition-colors cursor-pointer">
                                <span className="font-semibold">
                                  {getFilterTypeDisplayName(filterType)}:
                                </span>
                                <span className="ml-1">
                                  {filterType === "featured" ? (
                                    selectedNames.join(", ")
                                  ) : selectedIds.length <= 1 ? (
                                    selectedNames.join(", ")
                                  ) : (
                                    <>
                                      {selectedNames.slice(0, 1).join(", ")}
                                      <span className="ml-1 text-xs bg-orange-300 text-orange-900 px-1.5 py-0.5 rounded-full">
                                        +{selectedIds.length - 1} More
                                      </span>
                                    </>
                                  )}
                                </span>
                                <button
                                  onClick={() => clearFilterType(filterType)}
                                  className="ml-2 text-white hover:text-orange-200 transition-colors"
                                  title={`Remove all ${getFilterTypeDisplayName(
                                    filterType
                                  ).toLowerCase()} filters`}
                                >
                                  <svg
                                    className="w-3 h-3"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </span>

                              {/* Dropdown for individual filter deletion */}
                              {filterType !== "featured" &&
                                selectedIds.length > 1 && (
                                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-max opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                                    <div className="p-2">
                                      <div className="text-xs font-semibold text-gray-700 mb-1 px-2">
                                        Remove individual{" "}
                                        {getFilterTypeDisplayName(
                                          filterType
                                        ).toLowerCase()}
                                        :
                                      </div>
                                      {selectedIds.map((filterId, index) => (
                                        <button
                                          key={filterId}
                                          onClick={() =>
                                            removeSidebarFilter(
                                              filterType,
                                              filterId
                                            )
                                          }
                                          className={`flex items-center justify-between w-full px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors ${
                                            index >= 1 ? "bg-gray-50" : ""
                                          }`}
                                        >
                                          <span className="flex items-center gap-2">
                                            {getFilterName(
                                              filterType,
                                              filterId
                                            )}
                                          </span>
                                          <svg
                                            className="w-3 h-3 text-gray-400 hover:text-red-500"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row w-full">
          {/* Mobile Sidebar - Full width overlay */}
          <div
            id="left-section"
            className={`bg-darkGraybg transition-all duration-300 ease-in-out lg:block ${
              leftVisible
                ? "fixed lg:static inset-0 lg:inset-auto z-50 lg:z-auto w-full lg:w-[20%] opacity-100 translate-x-0 lg:pointer-events-auto"
                : "hidden lg:block lg:w-0 lg:opacity-0 lg:-translate-x-10 lg:pointer-events-none"
            }`}
            style={{ overflow: leftVisible ? "auto" : "hidden" }}
          >
            {/* Mobile close button */}
            <div className="lg:hidden flex justify-end p-4">
              <button
                onClick={() => setLeftVisible(false)}
                className="text-white hover:text-orange-500 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <Leftsidebar
              ref={sidebarRef}
              onFiltersChange={handleSidebarFiltersChange}
            />
          </div>

          <div
            className={`w-full lg:w-full ${
              leftVisible && "lg:w-[80%]"
            } transition-all duration-300`}
          >
            {/* Browse Results Heading */}
            <div id="browse-results" className="p-2 sm:p-4 pb-2">
              <div className="flex flex-row justify-between items-center gap-1 sm:gap-4 mb-2">
                <h2 className="text-xs sm:text-xl lg:text-2xl font-bold text-gray-800 flex-shrink-0">
                  Browse{" "}
                  <span className="text-orange-500">
                    Results (
                    {loading
                      ? "..."
                      : Object.keys(sidebarFilters).some(
                          (key) =>
                            sidebarFilters[key] &&
                            (Array.isArray(sidebarFilters[key])
                              ? sidebarFilters[key].length > 0
                              : sidebarFilters[key])
                        )
                      ? vehicles.length
                      : totalVehicles}
                    )
                  </span>
                </h2>
                <div className="flex flex-row items-center gap-1 sm:gap-4 flex-shrink min-w-0">
                  {/* View Toggle Icons */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-1 sm:p-2 transition-colors ${
                        viewMode === "grid"
                          ? "text-orange-500 bg-orange-50"
                          : "text-gray-600 hover:text-orange-500"
                      } rounded`}
                      title="Grid View"
                    >
                      <svg
                        className="w-3 h-3 sm:w-5 sm:h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-1 sm:p-2 transition-colors ${
                        viewMode === "list"
                          ? "text-orange-500 bg-orange-50"
                          : "text-gray-600 hover:text-orange-500"
                      } rounded`}
                      title="List View"
                    >
                      <svg
                        className="w-3 h-3 sm:w-5 sm:h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Page Size Dropdown */}
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                    className="w-12 sm:w-auto px-1 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-md text-xs sm:text-base text-gray-700 bg-white hover:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value={24}>24</option>
                    <option value={36}>36</option>
                    <option value={48}>48</option>
                    <option value={72}>72</option>
                    <option value={96}>96</option>
                  </select>

                  {/* Sorting Dropdown */}
                  <select
                    value={sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="w-16 sm:w-auto px-1 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-md text-xs sm:text-base text-gray-700 bg-white hover:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="id-desc">Newest First</option>
                    <option value="id-asc">Oldest First</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="year-new">Year: Newest First</option>
                    <option value="year-old">Year: Oldest First</option>
                    <option value="mileage-low">Mileage: Low to High</option>
                    <option value="mileage-high">Mileage: High to Low</option>
                  </select>
                </div>
              </div>
              {/* Separator Line */}
              <hr className="border-orange-500 mb-4" />

              {/* Auction and Lane Info Display */}

              {/* {(auctionId && laneId) || selectedAuction ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg
                      className="w-5 h-5 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <h3 className="text-lg font-semibold text-blue-800">
                      Auction Information
                    </h3>
                  </div>

                  {(() => {
                    const currentAuctionId = selectedAuction || auctionId;
                    const currentLaneId = selectedLane || laneId;

                    const selectedAuctionData = auctions.find(
                      (auction) => auction.id.toString() === currentAuctionId
                    );

                    if (!selectedAuctionData) return null;

                    // Log auction and lanes information
                    console.log("=== AUCTION AND LANES INFORMATION ===");
                    console.log("Current Auction ID:", currentAuctionId);
                    console.log("Current Lane ID:", currentLaneId);
                    console.log("Selected Auction Data:", selectedAuctionData);
                    console.log(
                      "Auction Name:",
                      selectedAuctionData.name || selectedAuctionData.auction
                    );
                    console.log("Auction Date:", selectedAuctionData.startDate);
                    console.log(
                      "Available Lanes:",
                      selectedAuctionData.auctionLanes
                    );

                    if (
                      selectedAuctionData.auctionLanes &&
                      selectedAuctionData.auctionLanes.length > 0
                    ) {
                      console.log("=== ALL LANES IN CURRENT AUCTION ===");
                      console.log(
                        "Total Lanes:",
                        selectedAuctionData.auctionLanes.length
                      );

                      // Create a table format for better readability
                      console.table(
                        selectedAuctionData.auctionLanes.map((lane, index) => ({
                          "#": index + 1,
                          "Auction Lane ID": lane.id,
                          "Lane ID": lane.laneId,
                          "Lane Name": lane.lane?.name || `Lane ${lane.laneId}`,
                          "Run Number": lane.runNo || "N/A",
                          "Vehicle Count":
                            lane.auctionLaneVehicles?.length || 0,
                          "Auction ID": lane.auctionId || currentAuctionId,
                          "Is Selected":
                            lane.id?.toString() === currentLaneId ||
                            lane.laneId?.toString() === currentLaneId
                              ? "YES"
                              : "NO",
                        }))
                      );

                      // Also log individual lane details
                      console.log("Individual Lane Details:");
                      selectedAuctionData.auctionLanes.forEach(
                        (lane, index) => {
                          const isSelected =
                            lane.id?.toString() === currentLaneId ||
                            lane.laneId?.toString() === currentLaneId;
                          console.log(
                            `Lane ${index + 1} ${
                              isSelected ? "(SELECTED)" : ""
                            }:`,
                            {
                              auctionLaneId: lane.id,
                              laneId: lane.laneId,
                              laneName:
                                lane.lane?.name || `Lane ${lane.laneId}`,
                              runNo: lane.runNo,
                              vehicleCount:
                                lane.auctionLaneVehicles?.length || 0,
                              auctionId: lane.auctionId || currentAuctionId,
                              isSelected: isSelected,
                              fullLaneObject: lane,
                            }
                          );
                        }
                      );
                      console.log("=====================================");
                    } else {
                      console.log("No lanes available for this auction");
                    }
                    console.log("=====================================");

                    return (
                      <div className="space-y-2">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-blue-700">
                              Auction:
                            </span>
                            <span className="text-blue-600">
                              {selectedAuctionData.name ||
                                selectedAuctionData.auction}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-blue-700">
                              Date:
                            </span>
                            <span className="text-blue-600">
                              {new Date(
                                selectedAuctionData.startDate
                              ).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-blue-700">
                              Auction ID:
                            </span>
                            <span className="text-blue-600">
                              {currentAuctionId}
                            </span>
                          </div>
                        </div>

                        {currentLaneId && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-blue-700">
                              Selected Lane:
                            </span>
                            <span className="text-blue-600">
                              {(() => {
                                const lane =
                                  selectedAuctionData.auctionLanes?.find(
                                    (lane) =>
                                      lane.id?.toString() === currentLaneId ||
                                      lane.laneId?.toString() === currentLaneId
                                  );
                                return (
                                  lane?.lane?.name || `Lane ${currentLaneId}`
                                );
                              })()}
                            </span>
                            <span className="text-xs text-gray-500">
                              (ID: {currentLaneId})
                            </span>
                          </div>
                        )}

                        <div className="text-sm text-blue-600">
                          {currentLaneId
                            ? `Showing vehicles from the selected lane only`
                            : `Showing all vehicles from this auction`}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : null} */}

              {/* Auctions Dropdown and Lanes */}
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  <select
                    value={selectedAuction}
                    onChange={(e) => handleAuctionChange(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm sm:text-base text-gray-700 bg-white hover:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">All Auctions</option>
                    {auctions.length > 0 && (
                      <optgroup label="📅 Upcoming Auctions">
                        {auctions.map((auction) => (
                          <option key={auction.id} value={auction.id}>
                            {auction.name} -{" "}
                            {new Date(auction.startDate).toLocaleDateString()}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>

                  {/* Lanes Buttons */}
                  {selectedAuction &&
                    (() => {
                      const selectedAuctionData = auctions.find(
                        (auction) => auction.id.toString() === selectedAuction
                      );

                      // Get lanes from the processed auctionLanes
                      let lanes = [];
                      if (
                        selectedAuctionData &&
                        selectedAuctionData.auctionLanes
                      ) {
                        lanes = selectedAuctionData.auctionLanes;
                      }

                      return (
                        <div className="flex flex-col gap-2">
                          {lanes.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {lanes.map((auctionLane) => {
                                const auctionLaneId = auctionLane.id; // This is the auctionLaneId for filtering
                                const laneId = auctionLane.laneId; // This is the actual laneId
                                const laneName =
                                  auctionLane.lane?.name ||
                                  `Lane ${auctionLane.laneId}`;
                                const runNo = auctionLane.runNo;

                                // Log the mapping for debugging
                                console.log("Lane Button Mapping:", {
                                  laneName: laneName,
                                  auctionLaneId: auctionLaneId,
                                  laneId: laneId,
                                  runNo: runNo,
                                  fullAuctionLaneObject: auctionLane,
                                });

                                return (
                                  <button
                                    key={auctionLaneId}
                                    onClick={() => {
                                      console.log("Lane Button Clicked:", {
                                        buttonLaneName: laneName,
                                        auctionLaneId: auctionLaneId,
                                        laneId: laneId,
                                        willCallHandleLaneSelectWith:
                                          auctionLaneId,
                                      });
                                      handleLaneSelect(auctionLaneId);
                                    }}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${
                                      selectedLane === auctionLaneId ||
                                      laneId === auctionLaneId?.toString() ||
                                      laneId === laneId?.toString()
                                        ? "bg-orange-500 text-white hover:bg-orange-600"
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                    }`}
                                    title={`Lane ID: ${laneId}, Auction Lane ID: ${auctionLaneId}, Run #${runNo}`}
                                  >
                                    {laneName}
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded">
                              No lanes available for this auction
                            </div>
                          )}
                        </div>
                      );
                    })()}
                </div>
              </div>
              <div className="w-full h-0.5 bg-orange-500 mb-2 sm:mb-4"></div>
            </div>

            {/* Vehicle Results */}
            {loading ? (
              <div className="w-full p-8 flex justify-center items-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading vehicles...</p>
                </div>
              </div>
            ) : vehicles.length === 0 ? (
              <div className="w-full p-8 text-center">
                <p className="text-gray-600 text-lg">
                  No vehicles found matching your criteria.
                </p>
                <p className="text-gray-500 mt-2">
                  Try adjusting your filters or search terms.
                </p>
              </div>
            ) : viewMode === "grid" ? (
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 p-2 sm:p-4">
                {currentVehicles.map((vehicle) => (
                  <VehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    wishlistData={wishlistData}
                  />
                ))}
              </div>
            ) : (
              <div className="w-full p-2 sm:p-4">
                {currentVehicles.map((vehicle) => (
                  <VehicleListCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
            )}

            {/* Pagination - Only show if page size is greater than total count */}
            {pageSize < displayCount && (
              <div className="flex flex-col sm:flex-row justify-between items-center px-2 sm:px-4 py-4 border-t bg-gray-50">
                {/* Results Info */}
                <div className="text-sm text-gray-700 mb-2 sm:mb-0">
                  Showing {vehicles.length > 0 ? startIndex : 0} to {endIndex}{" "}
                  of{" "}
                  {Object.keys(sidebarFilters).some(
                    (key) =>
                      sidebarFilters[key] &&
                      (Array.isArray(sidebarFilters[key])
                        ? sidebarFilters[key].length > 0
                        : sidebarFilters[key])
                  )
                    ? vehicles.length
                    : totalVehicles}{" "}
                  results
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center space-x-1">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      currentPage === 1
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    Previous
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => {
                      // Show first page, last page, current page, and pages around current page
                      const showPage =
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1);

                      if (!showPage) {
                        // Show ellipsis for gaps
                        if (page === 2 && currentPage > 4) {
                          return (
                            <span
                              key={page}
                              className="px-2 py-2 text-gray-500"
                            >
                              ...
                            </span>
                          );
                        }
                        if (
                          page === totalPages - 1 &&
                          currentPage < totalPages - 3
                        ) {
                          return (
                            <span
                              key={page}
                              className="px-2 py-2 text-gray-500"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm rounded-md transition-colors ${
                            page === currentPage
                              ? "bg-orange-500 text-white"
                              : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    }
                  )}

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      currentPage === totalPages
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Search;
